import React, { useState, useEffect } from 'react'
import './ClientEsriDashboards.css'
import PortalHeader from '../clientNavbar/PortalHeader'

// Components
import { useAuth } from '../../Auth';

// Assets
import tempImage from '../client-dashboard-assets/esri-card-pic.jpg';

// Icons
import { HiDocumentText } from "react-icons/hi2";
import { BsLightningFill } from "react-icons/bs";
import { LuUserRound } from "react-icons/lu";
import { IoInformationCircleOutline } from "react-icons/io5";
import { Link } from 'react-router-dom';
import { FaPlayCircle } from "react-icons/fa";


// Single interface to represent an ESRI card with its links
interface EsriLink {
    EsriID: number;
    linkType: 'dashboard' | 'user_guide' | 'tutorial_video';
    linkURL: string;
}

interface EsriCard {
    cardID: number;
    dashboard_title: string;
    background_image: string;
    esri_links: EsriLink[];
}

interface OrgData {
    esri_cards: EsriCard[];
}


const ClientEsriDashboards = () => {

    const [cards, setCards] = useState<EsriCard[]>([]);
    const { isAuthenticated, getDashboardPermissions } = useAuth();
    const [clientPermissionGroups, setClientPermissionGroups] = useState<{ [key: string]: any }>({});

    const [multiDashboardPopup, setMultiDashboardPopup] = useState<{
        groupName: string;
        dashboards: [string, any][];
    } | null>(null);


    const [selectedDashboard, setSelectedDashboard] = useState<{
        title: string;
        links: EsriLink[];
        background_image: string;
    } | null>(null);



    // Code for handling fadeout closing animation
    const [isClosing, setIsClosing] = useState(false);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setSelectedDashboard(null); // Unmount after fade-out
            setIsClosing(false); // Reset for next open
        }, 500); // Match this to your fadeOut duration (0.5s)
    };

    // Ensure the page always start at the top
    useEffect(() => {
        window.scrollTo(0, 0); // Scroll to the top when the component mounts
    }, []);

    // Fetching from Client Groups
    useEffect(() => {
        const fetchClientPermissions = async () => {
            const permissions2 = await getDashboardPermissions();
            setClientPermissionGroups(permissions2 || {});
        };

        if (isAuthenticated) {
            fetchClientPermissions();
        }
    }, [isAuthenticated, getDashboardPermissions]);

    useEffect(() => {
        const fetchOrgCards = async () => {
            try {
                const response = await fetch('/portal/api/org_view/', {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin',
                });

                if (!response.ok) throw new Error('Failed to fetch org data');

                const data: OrgData[] = await response.json();

                // org_view always returns 1 entry
                if (data.length > 0) {
                    setCards(data[0].esri_cards);
                }
            } catch (err) {
                console.error(err);
            }
        };

        fetchOrgCards();
    }, []);

    return (
        <div className='esri-dash-hub-wrapper'>

            <PortalHeader />

            <div className='esri-dash-hub-card-stack'>
                {cards.map(card => {
                    const isSingleDashboard = true; // each card is treated as a single dashboard
                    const matchedCardImage = card.background_image || tempImage;

                    return (
                        <div
                            key={card.cardID}
                            className="theCard single-dashboard"
                            onClick={() => setSelectedDashboard({
                                title: card.dashboard_title,
                                links: card.esri_links,
                                background_image: card.background_image || tempImage // <-- add this
                            })}

                        >
                            <div className="card-inner">
                                <div
                                    className="thefront image-bg"
                                    style={{ backgroundImage: `url(${matchedCardImage})` }}
                                >
                                    <div className="hover-overlay" />
                                    <div className="front-pilot-info">
                                        <div className="front-overlay" />
                                        <h1 className='esri-title'>{card.dashboard_title}</h1>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>


            {selectedDashboard && (
                <div
                    className={`esri-dash-fullscreen ${isClosing ? 'fade-out' : 'fade-in'}`}
                    style={{
                        backgroundImage: `url(${selectedDashboard.background_image || tempImage})`,
                    }}
                >
                    <div className="esri-dash-overlay" />

                    <div className="esri-dash-content">
                        <h2>{selectedDashboard.title}</h2>

                        <div className="esri-dash-links">
                            {selectedDashboard.links.map((link: EsriLink) => {
                                if (!link.linkURL) return null;
                                let icon = null;
                                let label = '';

                                switch (link.linkType) {
                                    case 'dashboard': icon = <BsLightningFill className="esriIcons" />; label = 'Dashboard'; break;
                                    case 'user_guide': icon = <HiDocumentText className="esriIcons" />; label = 'User Guide'; break;
                                    case 'tutorial_video': icon = <FaPlayCircle className="esriIcons" />; label = 'Tutorial Video'; break;
                                }

                                return (
                                    <a key={link.EsriID} href={link.linkURL} target="_blank" rel="noopener noreferrer">
                                        {icon} {label}
                                    </a>
                                );
                            })}

                            <Link to='/fnn_portal_esri_credentials'>
                                <LuUserRound className="esriIcons" /> Esri User Info
                            </Link>
                        </div>

                    </div>

                    <button className="close-button" onClick={handleClose}>
                        Close
                    </button>
                </div>
            )}


            {multiDashboardPopup && (
                <div className="sub-popup-overlay">
                    <div className="sub-popup-content for-esri">

                        <button
                            className="close-popup-button"
                            onClick={() => setMultiDashboardPopup(null)}
                        >
                            x
                        </button>

                        <h2>{multiDashboardPopup.groupName.replace('detectors_', '').replace('_', ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</h2>
                        <ul className="sub-popup-dashboard-list">
                            {multiDashboardPopup.dashboards.map(([title, links], idx) => (
                                <li key={idx} onClick={() => {
                                    setSelectedDashboard({
                                        title,
                                        links,
                                        background_image: cards.find(c => c.dashboard_title === title)?.background_image || tempImage
                                    });
                                    setMultiDashboardPopup(null);
                                }}>
                                    {title}
                                </li>

                            ))}
                        </ul>
                    </div>
                </div>
            )}






        </div>
    )
}

export default ClientEsriDashboards